import React, {useEffect, useState, useRef} from "react";
import DisplayCard, {CARD_ASPECT_RATIO} from "../components/DisplayCard";


const ArkNovaBoardPreview = ({player}) => {
    const mapName = player?.mapName || null;
    const safeMapName = (mapName || '').toString().toLowerCase();
    const src = safeMapName ? `/public/arknova/maps/map_${safeMapName}.png` : null;

    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const buildingImgCache = useRef({});
    const bonusImgCache = useRef({});
    const blockingBuilding2ImgCache = useRef(null);

    // --- Hex/grid helpers (kept consistent with BuildingState) ---
    const HEX = {
        R: 71,
        ORIG_CENTER_X: 84,
        ORIG_CENTER_Y_FROM_BOTTOM: 207,
    };

    const getMapTransform = (canvas, img) => {
        const rect = canvas.getBoundingClientRect();
        const cssSize = Math.max(0, Math.floor(rect.width));
        const scale = Math.min(cssSize / img.width, cssSize / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const offsetX = (cssSize - drawW) / 2;
        const offsetY = (cssSize - drawH) / 2;
        return {rect, cssSize, scale, offsetX, offsetY};
    };

    const hexToOriginalPx = (img, i, j) => {
        const r = HEX.R;
        const ORIG_CENTER_Y = img.height - HEX.ORIG_CENTER_Y_FROM_BOTTOM;
        const x = HEX.ORIG_CENTER_X + 1.5 * r * i;
        const y = ORIG_CENTER_Y - (Math.sqrt(3) * r) * j + (i % 2 === 1 ? Math.sqrt(3) * 0.5 * r : 0);
        return {x, y};
    };

    const originalPxToCanvasCss = (t, p) => ({x: t.offsetX + p.x * t.scale, y: t.offsetY + p.y * t.scale});

    const draw = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const parent = canvas.parentElement;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const cssSize = Math.max(0, Math.floor(rect.width));
        const dpr = window.devicePixelRatio || 1;

        canvas.style.width = cssSize + 'px';
        canvas.style.height = cssSize + 'px';
        canvas.width = Math.max(1, Math.floor(cssSize * dpr));
        canvas.height = Math.max(1, Math.floor(cssSize * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const img = imgRef.current;
        ctx.clearRect(0, 0, cssSize, cssSize);
        if (!img) return;

        const t = getMapTransform(canvas, img);
        ctx.drawImage(img, t.offsetX, t.offsetY, img.width * t.scale, img.height * t.scale);

        const zooMap = player?.zooMap;
        if (!zooMap) return;

        const getBuildingImg = (building) => {
            if (!building?.name) return null;
            if (buildingImgCache.current[building.name]) return buildingImgCache.current[building.name];
            const im = new Image();
            im.src = `/public/arknova/buildings/building_${building.name.toLowerCase()}${building.name.startsWith("ENCLOS") && !building.occupied ? '_empty' : ''}.png`;
            im.onload = () => draw();
            buildingImgCache.current[building.name] = im;
            return im;
        };

        const getBonusImg = (bonus) => {
            if (!bonus?.bonusName) return null;
            if (bonusImgCache.current[bonus.bonusName]) return bonusImgCache.current[bonus.bonusName];
            const im = new Image();
            im.src = `/public/arknova/bonus/bonus_${bonus.bonusName.toLowerCase()}.png`;
            im.onload = () => draw();
            bonusImgCache.current[bonus.bonusName] = im;
            return im;
        };

        // bonuses
        if (zooMap.bonuses) {
            Object.keys(zooMap.bonuses).forEach(i => Object.keys(zooMap.bonuses[i]).forEach(j => {
                const bonus = zooMap.bonuses[i][j];
                const bimg = getBonusImg(bonus);
                if (!bimg || !bimg.complete) return;
                const orig = hexToOriginalPx(img, parseInt(i), parseInt(j));
                const c = originalPxToCanvasCss(t, orig);
                const imgW = 70 * t.scale * bimg.width / bimg.height;
                const imgH = 70 * t.scale;
                ctx.save();
                ctx.translate(c.x, c.y);
                ctx.drawImage(bimg, -imgW / 2, -imgH / 2, imgW, imgH);
                if (bonus.value && (bonus.value > 1 || bonus.bonusName === "REPUTATION")) {
                    ctx.fillStyle = "white";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.font = `${Math.floor(imgH * 0.7)}px Arial`;
                    ctx.fillText("" + bonus.value, 0, 0);
                }
                ctx.restore();
            }));
        }

        // blocking building 2 overlay
        if (zooMap.blockingBuilding2) {
            Object.keys(zooMap.blockingBuilding2).forEach(iKey => Object.keys(zooMap.blockingBuilding2[iKey].values).forEach(jKey => {
                let bimg;
                const i = parseInt(iKey);
                const j = parseInt(zooMap.blockingBuilding2[iKey].values[jKey].value);
                if (blockingBuilding2ImgCache.current) {
                    bimg = blockingBuilding2ImgCache.current;
                } else {
                    bimg = new Image();
                    bimg.src = `/public/arknova/bonus/icon_requirement_building_2.png`;
                    bimg.onload = () => draw();
                    blockingBuilding2ImgCache.current = bimg;
                }
                if (!bimg || !bimg.complete) return;
                const orig = hexToOriginalPx(img, i, j);
                const c = originalPxToCanvasCss(t, orig);
                const imgW = 70 * t.scale * bimg.width / bimg.height;
                const imgH = 70 * t.scale;
                ctx.save();
                ctx.translate(c.x, c.y);
                ctx.drawImage(bimg, -imgW / 2, -imgH / 2, imgW, imgH);
                ctx.restore();
            }));
        }

        // placed buildings
        const buildings = zooMap.buildings || [];
        buildings.forEach(pt => {
            const bimg = getBuildingImg(pt);
            if (!bimg || !bimg.complete) return;
            const orig = hexToOriginalPx(img, pt.i, pt.j);
            const c = originalPxToCanvasCss(t, orig);
            const imgW = 570 * t.scale;
            const imgH = 500 * t.scale;
            ctx.save();
            ctx.translate(c.x, c.y);
            ctx.rotate(Math.PI / 3 * (pt.nbRotations || 0));
            ctx.drawImage(bimg, -imgW / 2, -imgH / 2, imgW, imgH);
            ctx.restore();
        });
    };

    useEffect(() => {
        if (!src) {
            imgRef.current = null;
            draw();
            return;
        }
        const img = new Image();
        img.src = src;
        img.onload = () => {
            imgRef.current = img;
            draw();
        };
        img.onerror = () => {
            imgRef.current = null;
            draw();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    useEffect(() => {
        draw();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [player?.zooMap]);

    useEffect(() => {
        const onResize = () => draw();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const zooMap = player?.zooMap;
    const mapDisplayName = zooMap?.displayName;
    const mapInternalName = zooMap?.name;
    const mapDescription = zooMap?.description;

    const effectImgSrc = mapInternalName
        ? `/public/arknova/maps/${mapInternalName.toString().toLowerCase()}_effect.png`
        : null;

    if (!src) {
        return (
            <div style={{width: '100%', height: '100%', overflow: 'auto', padding: '12px', boxSizing: 'border-box'}}>
                <div style={{color: 'white'}}>
                    Plateau indisponible (mapName manquant).
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'auto',
                padding: 0,
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            <div style={{width: '100%', maxWidth: 'min(92vh, 100%)'}}>
                {mapDisplayName && (
                    <div style={{
                        width: '100%',
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 700,
                        margin: '10px 0 8px 0'
                    }}>
                        {mapDisplayName}
                    </div>
                )}

                <canvas
                    ref={canvasRef}
                    style={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        height: 'auto',
                        display: 'block',
                        background: '#ffffff00'
                    }}
                />

                <div style={{
                    width: '100%',
                    padding: '12px 8px 16px 8px',
                    boxSizing: 'border-box'
                }}>
                    <div style={{
                        width: '100%',
                        background: '#191b31',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'stretch',
                        padding: '12px',
                        boxSizing: 'border-box',
                        gap: '12px'
                    }}>
                        <div style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                width: '100%',
                                maxWidth: '140px',
                                height: '100%',
                                padding: '8px',
                                boxSizing: 'border-box',
                                borderRadius: '8px',
                                background: 'rgba(255,255,255,0.04)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {effectImgSrc && (
                                    <img
                                        src={effectImgSrc}
                                        alt={'map effect ' + (mapInternalName || '')}
                                        style={{width: '100%', height: '100%', objectFit: 'contain', display: 'block'}}
                                    />
                                )}
                            </div>
                        </div>

                        <div style={{
                            flex: 4,
                            minWidth: 0,
                            padding: '4px 6px',
                            boxSizing: 'border-box',
                            lineHeight: 1.35,
                            borderLeft: '1px solid rgba(255,255,255,0.08)'
                        }}>
                            <div style={{whiteSpace: 'pre-wrap'}}>
                                {mapDescription || ''}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ConsultScreen = (props) => {

    const consults = [];
    consults.push("Hand");
    consults.push("Played");
    consults.push("River");
    consults.push("Endgame");
    consults.push("Aide");

    const [consult, setConsult] = useState(consults[0]);
    const [helpTab, setHelpTab] = useState("Actions");
    const [state, setState] = useState({card: false, selectedAction: null, showImprovedPreview: false});

    const player = props.state.players[props.userId];

    const isInActionsHelp = consult === "Aide" && helpTab === "Actions";

    useEffect(() => {
        // When opening the Actions help tab (or when actionCards appear), default to the first action.
        if (!isInActionsHelp) return;
        const first = (player && player.actionCards && player.actionCards.length > 0) ? player.actionCards[0] : null;
        if (!first) return;
        if (state.selectedAction) return;
        setState((prev) => ({...prev, selectedAction: first, showImprovedPreview: false}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInActionsHelp, player && player.actionCards && player.actionCards.length]);

    const actionIconSrc = (action) => "/public/arknova/icon_action_" + action + ".png";
    const isImprovedAction = (action) => {
        // action is one of: ASSOCIATION, BUILDING, ANIMAL, SPONSOR, CARD
        const map = {
            CARD: "hasImprovedCard",
            ASSOCIATION: "hasImprovedAssociation",
            BUILDING: "hasImprovedBuilding",
            SPONSOR: "hasImprovedSponsor",
            ANIMAL: "hasImprovedAnimal",
        };
        const key = map[action];
        return !!(key && player && player[key]);
    };

    const isShowingImproved = (action) => {
        if (!action) return false;
        // If the player truly has improved, always show improved.
        if (isImprovedAction(action)) return true;
        // Otherwise, show improved only when preview is toggled on.
        return !!state.showImprovedPreview;
    };

    const actionCardSrc = (action) => {
        const suffix = isShowingImproved(action) ? "_0_2.png" : "_0_1.png";
        return "/public/arknova/cards/actions/card_action_" + (action + suffix).toLowerCase();
    };

    const renderActionsHelp = () => (
        <div style={{width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
            <div style={{
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '12px',
                padding: '8px 12px',
                boxSizing: 'border-box'
            }}>
                {(player.actionCards || []).map((action) => {
                    const isActive = state.selectedAction === action;
                    const isImproved = isImprovedAction(action);
                    return (
                        <div
                            key={action}
                            onClick={() => setState({...state, selectedAction: action, showImprovedPreview: false})}
                            style={{
                                width: '30%',
                                minWidth: '120px',
                                maxWidth: '220px',
                                aspectRatio: '2 / 1',
                                backgroundColor: isActive
                                    ? 'rgb(91, 192, 222)'
                                    : (isImproved ? 'rgb(165, 36, 123)' : 'rgb(0, 174, 239)'),
                                borderRadius: '999px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: isActive ? '0 0 0 3px rgba(255,255,255,0.35) inset' : 'none'
                            }}>
                            <img
                                src={actionIconSrc(action)}
                                alt={action}
                                style={{height: '60%', width: 'auto'}}
                            />
                        </div>
                    );
                })}
            </div>

            <div style={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                gap: '10px',
                paddingBottom: '8px'
            }}>

                {state.selectedAction && (
                    <img
                        src={actionCardSrc(state.selectedAction)}
                        alt={'action ' + state.selectedAction}
                        style={{
                            height: '100%',
                            width: 'auto',
                            maxWidth: '100%',
                            objectFit: 'contain'
                        }}
                    />
                )}

                {state.selectedAction && !isImprovedAction(state.selectedAction) && (
                    <div
                        onClick={() => setState({...state, showImprovedPreview: !state.showImprovedPreview})}
                        style={{
                            padding: '10px 14px',
                            borderRadius: '999px',
                            backgroundColor: state.showImprovedPreview ? 'rgb(0, 174, 239)' : 'rgb(165, 36, 123)',
                            color: 'white',
                            fontWeight: 600,
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}
                    >
                        {state.showImprovedPreview ? 'Voir version de base' : 'Voir version améliorée'}
                    </div>
                )}
            </div>
        </div>
    );

    const renderBoardHelp = () => {
        return <ArkNovaBoardPreview player={player} />;
    };

    let compConsult = <div
        style={{
            width: "100%", height: "88%",
            marginTop: "-1px"
        }}>
        <div style={{
            height: "88%",
            width: "100%",
            paddingTop: "2%",
            display: "inline-block",
            whiteSpace: "nowrap"
        }}>
            {
                consult === "Hand" && <div style={{width: '100%', overflow: 'auto'}}>

                    {player.hand.map(card =>
                        <div key={card.name}
                             style={{
                                 height: "64vh",
                                 maxHeight: "360px",
                                 aspectRatio: CARD_ASPECT_RATIO,
                                 margin: "20px 3vw",
                                 display: "inline-block",
                                 verticalAlign: "middle"
                             }}>
                            <DisplayCard card={card} adaptInfo={true} player={player}/>
                        </div>
                    )}
                </div>
            }
            {
                consult === "Played" && <div style={{width: '100%', overflow: 'auto'}}>
                    {player.sponsorsInPlay.map(card =>
                        <div key={card.name}
                             style={{
                                 height: "64vh",
                                 maxHeight: "360px",
                                 aspectRatio: CARD_ASPECT_RATIO,
                                 margin: "20px 3vw",
                                 display: "inline-block",
                                 verticalAlign: "middle"
                             }}>
                            <DisplayCard card={card}/>
                        </div>
                    )}
                </div>
            }
            {
                consult === "Played" && <div style={{width: '100%', overflow: 'auto'}}>
                    {player.animalsInPlay.map(card =>
                        <div key={card.name}
                             style={{
                                 height: "64vh",
                                 maxHeight: "360px",
                                 aspectRatio: CARD_ASPECT_RATIO,
                                 margin: "20px 3vw",
                                 display: "inline-block",
                                 verticalAlign: "middle"
                             }}>
                            <DisplayCard card={card}/>
                        </div>
                    )}
                </div>
            }
            {
                consult === "River" && <div style={{width: '100%', overflow: 'auto'}}>
                    {props.state.river.map(card =>
                        <div key={card.name}
                             style={{
                                 height: "64vh",
                                 maxHeight: "360px",
                                 aspectRatio: CARD_ASPECT_RATIO,
                                 margin: "20px 3vw",
                                 display: "inline-block",
                                 verticalAlign: "middle"
                             }}>
                            <DisplayCard card={card} adaptInfo={true} player={player}/>
                        </div>
                    )}
                </div>
            }
            {
                consult === "Endgame" && <div style={{width: '100%', overflow: 'auto'}}>

                    {player.endgameCards.map(card =>
                        <div key={card.name}
                             style={{
                                 height: "64vh",
                                 maxHeight: "360px",
                                 aspectRatio: CARD_ASPECT_RATIO,
                                 margin: "20px 3vw",
                                 display: "inline-block",
                                 verticalAlign: "middle"
                             }}>
                            <DisplayCard card={card}/>
                        </div>
                    )}
                </div>
            }
            {
                consult === "Aide" && <div style={{width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                    <div style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        boxSizing: 'border-box'
                    }}>
                        {['Actions', 'Plateau'].map(t => {
                            const active = helpTab === t;
                            return (
                                <div
                                    key={t}
                                    className={"board-game-home-top-button board-game-home-top-button-" + (active ? "active" : "inactive")}
                                    onClick={() => setHelpTab(t)}
                                    style={{height: 'auto', padding: '8px 14px'}}
                                >
                                    {t}
                                </div>
                            );
                        })}
                    </div>

                    <div style={{flex: 1, minHeight: 0}}>
                        {helpTab === 'Actions' && renderActionsHelp()}
                        {helpTab === 'Plateau' && renderBoardHelp()}
                    </div>
                </div>
            }
        </div>
    </div>;

    return (
        <div style={{width: "100%"}}>
            <div className="board-game-home-top-bar" style={{height: "8vh"}}>
                {
                    consults.map(c => <div key={c}
                                           className={"board-game-home-top-button board-game-home-top-button-" + (consult === c ? "active" : "inactive")}
                                           onClick={() => {
                                               setConsult(c)
                                           }}>
                        {c}
                    </div>)
                }
            </div>
            {
                compConsult
            }
        </div>
    )

};

export default ConsultScreen;
